<script>
import cardValidator from "card-validator";
import documentsValidator from "@/utils/validators/documents";
import { mapActions } from "vuex";
import errorMixin from "@/mixins/errorMixin";
import scrollMixin from "@/mixins/scrollMixin";
import Tooltip from "@/components/_Shared/Tooltip";

export default {
  mixins: [errorMixin, scrollMixin],
  props: {
    payable: {
      type: Object,
      required: true
    }
  },

  components: {
    Tooltip
  },

  data() {
    return {
      number: null,
      birthdate: null,
      name: null,
      document: null,
      deadline_month: null,
      deadline_year: null,
      cvc: null,
      phone_number: null,
      phone: {
        country_code: "55",
        area_code: null,
        number: null
      },
      preloader: false,
      tooltipCVV: "Código de verificação com 3 ou 4 dígitos no verso do cartão"
    };
  },

  methods: {
    ...mapActions("payments", ["paymentCreditCard"]),
    ...mapActions("orders", ["createOnGateway"]),
    async submit() {
      this.preloader = true;

      try {
        await this.validatesCardData();
        await this.validatesDocument();

        if (
          this.payable.type === "orders" &&
          this.payable.status === "scheduled"
        )
          await this.createOnGateway(this.payable.id);
        else await this.createPayment();
      } catch (e) {
        this.$toasted.error(e);
      }

      this.preloader = false;
    },

    async validatesCardData() {
      let validForm = await this.$validator.validateAll();
      let ccValidator = await cardValidator.number(this.number);

      if (!validForm)
        throw "Ops! Alguma informação está incorreta, verifique os dados!";
      else if (!ccValidator.isValid) {
        this.createError("number", "Número inválido!");
        throw "O número do cartão de crédito é inválido!";
      }
    },

    async validatesDocument() {
      let documentIsValid = documentsValidator.validate(this.document);

      if (!documentIsValid) {
        this.createError("document", "Documento inválido!");
        throw "O CPF ou CNPJ informado é inválido!";
      }
    },

    async createPayment() {
      try {
        let data = this.$data;
        let payable = this.payable;
        await this.paymentCreditCard({ data, payable });
      } catch (e) {
        this.$toasted.error(e);
      }
    }
  },

  watch: {
    phone_number(phone_number) {
      if (phone_number.length >= 10) {
        this.phone.area_code = phone_number.substr(0, 2);
        this.phone.number = phone_number.substr(2, phone_number.length);
      }
    }
  },

  channels: {
    OrderChannelCC: {
      received(data) {
        if (data.data.to_state === "created_on_gateway") this.createPayment();
      }
    }
  },

  mounted() {
    this.$cable.subscribe({ channel: "OrderChannel" }, "OrderChannelCC");
  }
};
</script>

<template>
  <div>
    <div class="mt-20 text-center">
      <a href="https://www.donamaid.com/termos-de-uso" target="_blank" class="accept">
        Ao finalizar a compra, você concorda com os nossos termos de uso
        <span>
          <img src="/static/url.svg" class="rel-icon" />
        </span>
      </a>
      <div class="sub-title payment-title mt-20">Pagar com cartão de crédito</div>
      <div class="accept">consulte as bandeiras aceitas:</div>
      <div class="flags">
        <img src="/static/visa.png" alt="Cartão bandeira Visa" />
        <img src="/static/mastercard.png" alt="Cartão bandeira Mastercard" />
        <img src="/static/hiper.png" alt="Cartão bandeira Hiper" />
        <img src="/static/elo.png" alt="Cartão bandeira Elo" />
        <img src="/static/diners.png" alt="Cartão bandeira Diners" />
        <img src="/static/amex.png" alt="Cartão bandeira Amex" />
        <img src="/static/itau.png" alt="Cartão bandeira Itaú" />
        <img src="/static/bradesco.png" alt="Cartão bandeira Bradesco" />
        <img src="/static/bb.png" alt="Cartão bandeira Banco do Brasil" />
        <img src="/static/banrisul.png" alt="Cartão bandeira Banrisul" />
      </div>
      <div class="fields" id="fields">
        <div class="field" :class="{'has-error': errors.first('number')}">
          <span>Número do cartão</span>
          <the-mask
            class="ym-disable-keys"
            type="text"
            name="number"
            :mask="'#### #### #### ####'"
            placeholder="0000 0000 0000 0000"
            v-validate="'required'"
            data-vv-as="Número do cartão"
            v-model="number"
          />
        </div>

        <div class="expiration-date-and-cvc">
          <div
            class="field"
            :class="{'has-error': errors.first('deadline_month') || errors.first('deadline_year')}"
          >
            <span>Data de expiração</span>
            <br />
            <select class="ym-disable-keys" name="deadline_month" v-validate="'required'" v-model="deadline_month">
              <option value="null">MÊS</option>
              <option value="01">01</option>
              <option value="02">02</option>
              <option value="03">03</option>
              <option value="04">04</option>
              <option value="05">05</option>
              <option value="06">06</option>
              <option value="07">07</option>
              <option value="08">08</option>
              <option value="09">09</option>
              <option value="10">10</option>
              <option value="11">11</option>
              <option value="12">12</option>
            </select>
            /
            <select
              class="ym-disable-keys"
              name="deadline_year"
              v-validate="'required'"
              v-model="deadline_year"
            >
              <option value="null">ANO</option>
              <option value="2019">2019</option>
              <option value="2020">2020</option>
              <option value="2021">2021</option>
              <option value="2022">2022</option>
              <option value="2023">2023</option>
              <option value="2024">2024</option>
              <option value="2025">2025</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
              <option value="2028">2028</option>
              <option value="2029">2029</option>
              <option value="2030">2030</option>
            </select>
          </div>

          <div class="field" :class="{'has-error': errors.first('cvc')}">
            <span>Código CVV</span>
            <div class=" tooltip-position">
              <Tooltip :title="tooltipCVV"></Tooltip>
            </div>

            <input
              class="ym-disable-keys"
              type="text"
              name="cvc"
              placeholder="000"
              maxlength="4"
              v-validate="'required|min:3|max:4|numeric'"
              v-model="cvc"
            />
          </div>
        </div>

        <div class="field" :class="{'has-error': errors.first('name')}">
          <span>Nome do titular do cartão</span>
          <input
            class="ym-disable-keys"
            type="text"
            name="name"
            placeholder="Paula S Silva"
            v-validate="'required'"
            data-vv-as="Nome do titular do cartão"
            v-model="name"
          />
        </div>

        <div class="field" :class="{'has-error': errors.first('document')}">
          <span>CPF ou CNPJ do titular</span>
          <the-mask
            class="ym-disable-keys"
            type="text"
            name="document"
            placeholder="000.000.000-00"
            v-validate="'required|min:11|max:14'"
            data-vv-as="CPF ou CNPJ do titular"
            :mask="['###.###.###-##', '##.###.###/####-##']"
            :masked="false"
            v-model="document"
          />
        </div>

        <div class="field" :class="{'has-error': errors.first('birthdate')}">
          <span>Data de nascimento</span>
          <input
            class="ym-disable-keys"
            type="text"
            name="birthdate"
            v-validate="'required|date_format:DD/MM/YYYY'"
            data-vv-as="Data de nascimento"
            placeholder="00/00/0000"
            v-mask="'##/##/####'"
            v-model="birthdate"
          />
        </div>

        <div class="field" :class="{'has-error': errors.first('phone')}">
          <span>Telefone</span>
          <the-mask
            class="ym-disable-keys"
            type="text"
            name="phone"
            v-validate="'required|numeric|min:10|max:11'"
            :mask="['(##) ####-####', '(##) #####-####']"
            placeholder="(00) 00000-0000"
            v-model="phone_number"
          />
        </div>
      </div>

      <div class="btn bg-pink btn-pay" :class="{'btn--loading': preloader}" @click="submit">
        <span>Pagar</span>
      </div>

      <div class="accept normal">Nossos pagamentos são processados pelo PAGBANK e ASAAS.</div>
    </div>
  </div>
</template>

<style scoped>
.rel-icon {
  height: 13px;
  width: 13px;
}

.tooltip-position {
  position: relative;
  top: 19px;
  right: 5px;
}

.fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 10px;
}

.expiration-date-and-cvc {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 10px;
}

.field.mobile {
  display: none;
}

.border-bottom {
  border-bottom: 1px solid #ddd;
}

.flags img {
  margin: 5px;
}

.payment-title {
  border-bottom: 1px solid var(--pink);
  width: 70%;
  margin: 0 auto;
  padding: 10px 0;
  text-transform: uppercase;
  font-size: 22px;
  font-weight: 700;
}

.field {
  display: inline-block;
  text-align: left;
  margin-top: 5px;
}

.field span {
  color: #333;
  font-size: 13px;
  font-weight: 300;
  line-height: 16px;
  margin: 10px 0 0;
  padding: 3px 0;
}

.field input {
  border: 1px solid #ddd;
  height: 35px;
  margin-top: 10px;
  width: calc(100% - 5px);
  border-radius: 4px;
}

.field.has-error input {
  border: 1px solid var(--red);
}

.field.has-error span {
  color: var(--red);
}

.field select {
  border: 1px solid #ddd;
  height: 35px;
  margin: 10px 0;
  /* width: 57px; */
  border-radius: 4px;
}

.accept {
  color: #333;
  font-size: 13px;
  font-weight: 300;
  line-height: 16px;
  margin: 10px 5px 0;
  padding: 3px 7px;
  font-style: italic;
}

.normal {
  font-style: normal;
}

.btn-pay {
  width: 100%;
  height: 16px;
  margin-top: 15px;
  margin-left: 0;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  padding: 18px 0;
}

/* BREAKPOINTS */

@media (max-width: 1200px) {
}

@media (max-width: 992px) {
}

@media (max-width: 768px) {
  .fields {
    grid-template-columns: 1fr;
  }
  .payment-title {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/_Shared/Checkout/Payment/CreditCard.vue